import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Clock, Volume2, VolumeX, Maximize2, Minimize2,
  Radio, TrendingUp, Activity, BarChart3, Signal,
  Send, MessageSquare,
  X, ChevronDown, ChevronUp, Pin, CornerUpLeft,
  ArrowDown, Sparkles, AlertTriangle,
} from 'lucide-react';
import { useAuthContext } from '../context/AuthContext';
import { useAppContext } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { calculateContracts, formatCurrency } from '../utils/sizing';
import { getMarketStatus } from '../utils/session';

const LOADING_STEPS = [
  'Establishing secure connection...',
  'Authenticating session...',
  'Syncing market data feed...',
  'Connecting to live stream...',
];

const CHAR_LIMIT = 200;
const RATE_WINDOW = 20000;
const RATE_MAX = 3;

const QUICK_EMOJIS = [
  '🔥','👍','📈','💰','🚀','💪','🎯','✅','❌','😂',
  '👀','⚡','💎','🐂','🐻','📊','💵','🙏','👏','🤝',
];

const INITIAL_MESSAGES = [];

const PINNED_MESSAGE = {
  user: 'AlphaBot',
  text: 'SELL signal triggered — 21,488.50 | SL 21,505 | TP 21,455',
  time: '9:47 AM',
};

const StreamPage = () => {
  const auth = useAuthContext();
  const app = useAppContext();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadStep, setLoadStep] = useState(0);
  const [loadProgress, setLoadProgress] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [hideTimeout, setHideTimeout] = useState(null);

  // Market session state
  const [market, setMarket] = useState(getMarketStatus);
  useEffect(() => {
    const t = setInterval(() => setMarket(getMarketStatus()), 60000);
    return () => clearInterval(t);
  }, []);
  const isLive = market.status === 'live';

  // Chat state
  const [chatMsg, setChatMsg] = useState('');
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showGuidelines, setShowGuidelines] = useState(() => !localStorage.getItem('anq_chat_rules_dismissed'));
  const [pinnedCollapsed, setPinnedCollapsed] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const [botTyping, setBotTyping] = useState(false);
  const [showMention, setShowMention] = useState(false);

  const chatEndRef = useRef(null);
  const messagesRef = useRef(null);
  const inputRef = useRef(null);
  const botTimerRef = useRef(null);
  const messagesSnapshotRef = useRef([]);
  const botPostTimesRef = useRef([]);

  useEffect(() => { messagesSnapshotRef.current = messages; }, [messages]);

  // Auth + market-hours guard — the livestream is only accessible during
  // live NY-session windows. Redirect back to the dashboard otherwise.
  useEffect(() => {
    if (!auth.isLoggedIn || !auth.isSubscribed) {
      navigate('/dashboard');
      return;
    }
    if (!isLive) {
      toast.info(
        market.countdown
          ? `Stream is offline — next session opens in ${market.countdown}.`
          : 'Stream is offline — come back during the next NY session.'
      );
      app.setIsFullscreen(false);
      navigate('/dashboard');
    }
  }, [auth.isLoggedIn, auth.isSubscribed, isLive, market.countdown, navigate, toast, app]);

  // Loader
  useEffect(() => {
    if (!loading) return;
    const stepDuration = 3500 / LOADING_STEPS.length;
    const progressInterval = setInterval(() => {
      setLoadProgress(prev => prev >= 100 ? 100 : prev + 0.8);
    }, 28);
    const stepInterval = setInterval(() => {
      setLoadStep(prev => prev >= LOADING_STEPS.length - 1 ? prev : prev + 1);
    }, stepDuration);
    const doneTimer = setTimeout(() => {
      setLoading(false);
    }, 3500);
    return () => { clearInterval(progressInterval); clearInterval(stepInterval); clearTimeout(doneTimer); };
  }, [loading]);

  // Scroll to bottom on mount
  useEffect(() => {
    chatEndRef.current?.scrollIntoView();
  }, [loading]);

  // Auto-scroll when at bottom
  useEffect(() => {
    if (isAtBottom) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setUnreadCount(0);
    } else {
      setUnreadCount(prev => prev + 1);
    }
  }, [messages]);

  // Scroll position detection
  const handleScroll = useCallback(() => {
    const el = messagesRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setIsAtBottom(atBottom);
    if (atBottom) setUnreadCount(0);
  }, []);

  const jumpToLatest = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setIsAtBottom(true);
    setUnreadCount(0);
  };


  const handleBack = () => { app.setIsFullscreen(false); navigate('/dashboard'); };
  const toggleFullscreen = () => app.setIsFullscreen(!app.isFullscreen);
  const toggleMute = () => app.setIsMuted(!app.isMuted);

  const handlePlayerMouseMove = () => {
    setControlsVisible(true);
    if (hideTimeout) clearTimeout(hideTimeout);
    const t = setTimeout(() => setControlsVisible(false), 3000);
    setHideTimeout(t);
  };

  // Send message
  const handleSendMsg = (e) => {
    e.preventDefault();
    const text = chatMsg.trim();
    if (!text || text.length > CHAR_LIMIT) return;

    const displayName = auth.user?.user_metadata?.display_name || auth.user?.email?.split('@')[0] || 'You';
    const timeStr2 = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    setMessages(prev => [...prev, {
      id: Date.now(),
      user: displayName,
      text,
      time: timeStr2,
      color: '#22d3ee',
      isMe: true,
      replyTo: replyingTo ? { user: replyingTo.user, text: replyingTo.text } : null,
    }]);
    setChatMsg('');
    setReplyingTo(null);
    setShowEmojiPicker(false);
    setShowMention(false);
    setIsAtBottom(true);

    handleBotMention(text);
  };

  // Guidelines dismiss
  const dismissGuidelines = () => {
    setShowGuidelines(false);
    localStorage.setItem('anq_chat_rules_dismissed', '1');
  };

  // AlphaBot: fetch a message from the API
  const fetchBotMessage = useCallback(async (type, userMessage, history) => {
    try {
      const res = await fetch('/api/chat-bot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, userMessage, history }),
      });
      const data = await res.json();
      return data.text || 'Stay disciplined out there.';
    } catch {
      return 'Patience is the most underrated edge in trading.';
    }
  }, []);

  // AlphaBot: append a bot message to chat
  const addBotMessage = useCallback((text) => {
    const timeStr2 = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    setMessages(prev => [...prev, {
      id: Date.now() + Math.random(),
      user: 'AlphaBot',
      text,
      time: timeStr2,
      color: '#a78bfa',
      isBot: true,
    }]);
  }, []);

  // AlphaBot: random wisdom timer
  // Cadence: random 5–15 min between posts, max 5 posts/hour, market hours only.
  const scheduleNextWisdom = useCallback(() => {
    const FIVE_MIN = 5 * 60 * 1000;
    const FIFTEEN_MIN = 15 * 60 * 1000;
    const delay = FIVE_MIN + Math.random() * (FIFTEEN_MIN - FIVE_MIN);

    botTimerRef.current = setTimeout(async () => {
      // Skip if market is closed — re-check on the next tick
      if (getMarketStatus().status !== 'live') {
        scheduleNextWisdom();
        return;
      }
      // Rate limit: max 5 bot posts per rolling hour
      const now = Date.now();
      botPostTimesRef.current = botPostTimesRef.current.filter(t => t > now - 60 * 60 * 1000);
      if (botPostTimesRef.current.length >= 5) {
        scheduleNextWisdom();
        return;
      }

      setBotTyping(true);
      const recentHistory = messagesSnapshotRef.current.slice(-10).map(m => ({
        role: m.isBot ? 'assistant' : 'user',
        content: m.text,
      }));
      const text = await fetchBotMessage('wisdom', null, recentHistory);
      const typingDelay = 1000 + Math.random() * 1500;
      setTimeout(() => {
        setBotTyping(false);
        addBotMessage(text);
        botPostTimesRef.current.push(Date.now());
        scheduleNextWisdom();
      }, typingDelay);
    }, delay);
  }, [fetchBotMessage, addBotMessage]);

  useEffect(() => {
    if (!loading) scheduleNextWisdom();
    return () => { if (botTimerRef.current) clearTimeout(botTimerRef.current); };
  }, [loading, scheduleNextWisdom]);

  // AlphaBot: respond to user messages
  const handleBotMention = useCallback(async (userText) => {
    const question = userText.replace(/@alphabot/gi, '').trim();
    if (!question) return;
    setBotTyping(true);
    const recentHistory = messages.slice(-10).map(m => ({
      role: m.isBot ? 'assistant' : 'user',
      content: m.text,
    }));
    const text = await fetchBotMessage('reply', question, recentHistory);
    const typingDelay = 800 + Math.random() * 1500;
    setTimeout(() => {
      setBotTyping(false);
      addBotMessage(text);
    }, typingDelay);
  }, [fetchBotMessage, addBotMessage, messages]);

  const timeStr = app.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  if (loading) {
    return (
      <div className="stream-loader">
        <div className="stream-loader__bg" />

        <div className="stream-loader__content">
          {/* Animated rings */}
          <div className="stream-loader__rings">
            <div className="stream-loader__ring stream-loader__ring--1" />
            <div className="stream-loader__ring stream-loader__ring--2" />
            <div className="stream-loader__ring stream-loader__ring--3" />
            <div className="stream-loader__icon">
              <Activity size={28} strokeWidth={1.5} />
            </div>
          </div>

          <h2 className="stream-loader__title">Connecting to Live Stream</h2>
          <p className="stream-loader__step">{LOADING_STEPS[loadStep]}</p>

          {/* Progress bar */}
          <div className="stream-loader__bar-track">
            <div
              className="stream-loader__bar-fill"
              style={{ width: `${Math.min(loadProgress, 100)}%` }}
            />
          </div>

          <div className="stream-loader__meta">
            <span>AlphaNQ</span>
            <span>{Math.min(Math.round(loadProgress), 100)}%</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`stream ${app.isFullscreen ? 'stream--fullscreen' : ''}`}>
      {!app.isFullscreen && (
        <div className="stream__topbar">
          <button className="stream__back" onClick={handleBack}>
            <ArrowLeft size={18} />
            <span>Dashboard</span>
          </button>

          <div className="stream__topbar-center">
            <div className={`stream__live-badge ${isLive ? '' : 'stream__live-badge--offline'}`}>
              {isLive ? <span className="stream__live-dot" /> : <Clock size={11} />}
              <span>{isLive ? 'LIVE' : 'OFFLINE'}</span>
            </div>
            <span className="stream__session-name">
              {isLive
                ? `${market.session} Nasdaq Futures Session`
                : market.label}
            </span>
          </div>

          <div className="stream__topbar-right">
            <Clock size={14} className="stream__clock-icon" />
            <span className="stream__clock">{timeStr}</span>
          </div>
        </div>
      )}

      <div className="stream__body">
        <div className="stream__layout">
          {/* Player column */}
          <div className="stream__main">
            <div
              className="stream__player"
              onMouseMove={handlePlayerMouseMove}
              onMouseLeave={() => setControlsVisible(false)}
            >
              <div className="stream__player-glow" />

              <div className="stream__placeholder">
                <div className="stream__placeholder-pulse" />
                <Activity size={48} strokeWidth={1.2} className="stream__placeholder-icon" />
                <h2 className="stream__placeholder-title">
                  {isLive ? 'Live Nasdaq Futures Trading' : market.label}
                </h2>
                <p className="stream__placeholder-sub">
                  {isLive
                    ? 'Stream is active — content will appear here'
                    : market.countdown
                      ? `Next session opens in ${market.countdown}. Come back then for live signals.`
                      : 'Come back during the next NY session for live signals.'}
                </p>
              </div>

              {isLive && (
                <div className="stream__rec">
                  <span className="stream__rec-dot" />
                  REC
                </div>
              )}

              <div className={`stream__controls ${controlsVisible ? 'stream__controls--visible' : ''}`}>
                <div className="stream__controls-left">
                  <button className="stream__ctrl-btn" onClick={toggleMute} title={app.isMuted ? 'Unmute' : 'Mute'}>
                    {app.isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                  </button>
                  <input
                    type="range"
                    className="stream__vol-slider"
                    min="0"
                    max="100"
                    value={app.isMuted ? 0 : app.volume}
                    onChange={(e) => {
                      app.setVolume(Number(e.target.value));
                      if (app.isMuted) app.setIsMuted(false);
                    }}
                  />
                  <span className="stream__vol-label">{app.isMuted ? 0 : app.volume}</span>
                </div>

                <div className="stream__controls-center">
                  <Signal size={14} />
                  <span>{isLive ? 'Connected' : 'Standby'}</span>
                </div>

                <div className="stream__controls-right">
                  <button className="stream__ctrl-btn" onClick={toggleFullscreen} title={app.isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                    {app.isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                  </button>
                </div>
              </div>
            </div>

            {!app.isFullscreen && (
              <div className="stream__info-strip">
                <div className="stream__info-item">
                  <Radio size={14} />
                  <span className="stream__info-label">Status</span>
                  <span className={`stream__info-value ${isLive ? 'stream__info-value--live' : 'stream__info-value--offline'}`}>
                    {isLive ? 'Live' : 'Closed'}
                  </span>
                </div>
                <div className="stream__info-divider" />
                <div className="stream__info-item">
                  <TrendingUp size={14} />
                  <span className="stream__info-label">Instrument</span>
                  <span className="stream__info-value">MNQ / NQ</span>
                </div>
                <div className="stream__info-divider" />
                <div className="stream__info-item">
                  <BarChart3 size={14} />
                  <span className="stream__info-label">Session</span>
                  <span className="stream__info-value">Regular Hours</span>
                </div>
                <div className="stream__info-divider" />
                <div className="stream__info-item">
                  <Activity size={14} />
                  <span className="stream__info-label">Signal</span>
                  <span className="stream__info-value stream__info-value--green">Strong</span>
                </div>
              </div>
            )}
          </div>

          {/* Chat panel */}
          {!app.isFullscreen && (
            <div className="stream-chat">
              {/* Header */}
              <div className="stream-chat__header">
                <div className="stream-chat__header-left">
                  <MessageSquare size={15} />
                  <span>AlphaBot Chat</span>
                </div>
                <div className="stream-chat__header-right">
                  <span className="stream-chat__ai-badge">AI</span>
                </div>
              </div>

              {/* Guidelines banner */}
              {showGuidelines && (
                <div className="stream-chat__guidelines">
                  <span>Ask AlphaBot anything about trading, discipline, or mindset.</span>
                  <button onClick={dismissGuidelines}>Got it</button>
                </div>
              )}

              {/* Pinned message */}
              <div className={`stream-chat__pinned ${pinnedCollapsed ? 'stream-chat__pinned--collapsed' : ''}`}>
                <button className="stream-chat__pinned-toggle" onClick={() => setPinnedCollapsed(!pinnedCollapsed)}>
                  <Pin size={12} />
                  <span>Pinned</span>
                  {pinnedCollapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
                </button>
                {!pinnedCollapsed && (
                  <div className="stream-chat__pinned-body">
                    <span className="stream-chat__pinned-user">{PINNED_MESSAGE.user}</span>
                    <span className="stream-chat__pinned-text">{PINNED_MESSAGE.text}</span>
                  </div>
                )}
              </div>

              {/* Messages */}
              <div className="stream-chat__messages" ref={messagesRef} onScroll={handleScroll}>
                {messages.map(msg => {
                  if (msg.signal) {
                    const profile = app.tradingProfile;
                    const sizing = profile
                      ? calculateContracts({
                          accountSize: profile.accountSize,
                          riskPercent: profile.riskPercent,
                          instrument: profile.instrument,
                          entry: String(msg.signal.entry).replace(/,/g, ''),
                          stop: String(msg.signal.sl).replace(/,/g, ''),
                          target: String(msg.signal.tp).replace(/,/g, ''),
                        })
                      : null;

                    return (
                      <div key={msg.id} className="stream-chat__signal-card">
                        <div className="stream-chat__signal-header">
                          <span className="stream-chat__name" style={{ color: msg.color }}>
                            {msg.user}
                          </span>
                          {msg.time && <span className="stream-chat__time">{msg.time}</span>}
                        </div>
                        <div className={`stream-chat__signal-body stream-chat__signal-body--${msg.signal.type.toLowerCase()}`}>
                          <span className={`stream-chat__signal-type stream-chat__signal-type--${msg.signal.type.toLowerCase()}`}>{msg.signal.type}</span>
                          <div className="stream-chat__signal-row">
                            <div className="stream-chat__signal-field">
                              <span className="stream-chat__signal-label">Entry</span>
                              <span className="stream-chat__signal-val">{msg.signal.entry}</span>
                            </div>
                            <div className="stream-chat__signal-field">
                              <span className="stream-chat__signal-label">SL</span>
                              <span className="stream-chat__signal-val stream-chat__signal-val--red">{msg.signal.sl}</span>
                            </div>
                            <div className="stream-chat__signal-field">
                              <span className="stream-chat__signal-label">TP</span>
                              <span className="stream-chat__signal-val stream-chat__signal-val--green">{msg.signal.tp}</span>
                            </div>
                          </div>
                        </div>

                        {/* Personalized sizing — only on signal cards */}
                        {sizing && !sizing.tooSmall && (
                          <div className="stream-chat__signal-yours">
                            <div className="stream-chat__signal-yours-head">
                              <Sparkles size={11} />
                              For your {formatCurrency(profile.accountSize, { short: true })} account
                            </div>
                            <div className="stream-chat__signal-yours-row">
                              <div className="stream-chat__yours-field">
                                <span className="stream-chat__yours-label">Size</span>
                                <span className="stream-chat__yours-val stream-chat__yours-val--accent">
                                  {sizing.contracts} {sizing.instrument}
                                </span>
                              </div>
                              <div className="stream-chat__yours-field">
                                <span className="stream-chat__yours-label">Risk</span>
                                <span className="stream-chat__yours-val stream-chat__yours-val--red">
                                  &minus;{formatCurrency(sizing.riskDollars)}
                                </span>
                              </div>
                              <div className="stream-chat__yours-field">
                                <span className="stream-chat__yours-label">Target</span>
                                <span className="stream-chat__yours-val stream-chat__yours-val--green">
                                  +{formatCurrency(sizing.rewardDollars)}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        {sizing && sizing.tooSmall && (
                          <div className="stream-chat__signal-yours stream-chat__signal-yours--warn">
                            <AlertTriangle size={11} />
                            Stop is too wide for your risk budget. Skip this trade or lower position to 0.
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div key={msg.id} className="stream-chat__msg">
                      {msg.replyTo && (
                        <div className="stream-chat__reply-ref">
                          <CornerUpLeft size={10} />
                          <span className="stream-chat__reply-ref-user">{msg.replyTo.user}</span>
                          <span className="stream-chat__reply-ref-text">{msg.replyTo.text.slice(0, 60)}{msg.replyTo.text.length > 60 ? '...' : ''}</span>
                        </div>
                      )}
                      <div className="stream-chat__msg-row">
                        <span className="stream-chat__name" style={{ color: msg.color }}>
                          {msg.user}
                        </span>
                        <span className="stream-chat__text">{msg.text}</span>
                        {msg.time && <span className="stream-chat__time">{msg.time}</span>}
                      </div>
                    </div>
                  );
                })}

                {botTyping && (
                  <div className="stream-chat__msg">
                    <div className="stream-chat__msg-row">
                      <span className="stream-chat__name" style={{ color: '#a78bfa' }}>AlphaBot</span>
                      <span className="stream-chat__text stream-chat__typing">typing<span className="stream-chat__dots">...</span></span>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Jump to latest */}
              {!isAtBottom && (
                <button className="stream-chat__jump" onClick={jumpToLatest}>
                  <ArrowDown size={13} />
                  {unreadCount > 0 ? `${unreadCount} new message${unreadCount > 1 ? 's' : ''}` : 'Jump to latest'}
                </button>
              )}

              {/* Reply banner */}
              {replyingTo && (
                <div className="stream-chat__reply-banner">
                  <CornerUpLeft size={12} />
                  <span>Replying to <strong>{replyingTo.user}</strong></span>
                  <span className="stream-chat__reply-preview">{replyingTo.text.slice(0, 50)}{replyingTo.text.length > 50 ? '...' : ''}</span>
                  <button className="stream-chat__reply-close" onClick={() => setReplyingTo(null)}><X size={13} /></button>
                </div>
              )}

              {/* @mention dropdown */}
              {showMention && (
                <div className="stream-chat__mention-dropdown">
                  <button
                    type="button"
                    className="stream-chat__mention-item"
                    onClick={() => {
                      const atIdx = chatMsg.lastIndexOf('@');
                      setChatMsg(chatMsg.slice(0, atIdx) + '@AlphaBot ');
                      setShowMention(false);
                      inputRef.current?.focus();
                    }}
                  >
                    <span className="stream-chat__mention-name">AlphaBot</span>
                    <span className="stream-chat__mention-role">AI Coach</span>
                  </button>
                </div>
              )}

              {/* Input */}
              <form className="stream-chat__input" onSubmit={handleSendMsg}>
                <div className="stream-chat__field-wrap">
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Ask AlphaBot something..."
                    value={chatMsg}
                    onChange={(e) => {
                    const val = e.target.value.slice(0, CHAR_LIMIT);
                    setChatMsg(val);
                    const atIdx = val.lastIndexOf('@');
                    if (atIdx !== -1 && (atIdx === 0 || val[atIdx - 1] === ' ')) {
                      const after = val.slice(atIdx + 1);
                      setShowMention(!after || 'alphabot'.startsWith(after.toLowerCase()));
                    } else {
                      setShowMention(false);
                    }
                  }}
                    className="stream-chat__field"
                  />
                  {chatMsg.length > 150 && (
                    <span className={`stream-chat__char-count ${chatMsg.length >= CHAR_LIMIT ? 'stream-chat__char-count--limit' : ''}`}>
                      {chatMsg.length}/{CHAR_LIMIT}
                    </span>
                  )}
                </div>
                <button type="submit" className="stream-chat__send" disabled={!chatMsg.trim() || chatMsg.length > CHAR_LIMIT}>
                  <Send size={16} />
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StreamPage;
